import React, { useState } from 'react';
import { MdClose, MdThumbUp, MdThumbDown, MdHandshake, MdExpandMore } from 'react-icons/md';

const PAGE_SIZE = 20;

function RepPollResponses({
  selectedTag,
  pollResponses,
  loadingPolls,
  repFirstName,
  agreedPolls,
  disagreedPolls,
  agreeingPoll,
  pollsRef,
  onAgree,
  onDisagree,
  onClose,
  isOwnProfile,
}) {
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);

  // Reset visible count when poll responses change
  const [prevLength, setPrevLength] = useState(0);
  if (pollResponses.length !== prevLength) {
    setPrevLength(pollResponses.length);
    setVisibleCount(PAGE_SIZE);
  }

  if (loadingPolls) {
    return (
      <div ref={pollsRef} className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      </div>
    );
  }

  if (!selectedTag) return <div ref={pollsRef} />;

  if (pollResponses.length === 0) {
    return (
      <div ref={pollsRef} className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 text-center text-gray-500">
        No poll responses found for this topic.
      </div>
    );
  }

  const visiblePolls = pollResponses.slice(0, visibleCount);
  const hasMore = visibleCount < pollResponses.length;
  const remaining = pollResponses.length - visibleCount;

  return (
    <div ref={pollsRef} className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-900">
          {selectedTag.tag} Polls ({pollResponses.length})
        </h3>
        <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
          <MdClose className="text-xl" />
        </button>
      </div>

      <div className="space-y-4">
        {visiblePolls.map((poll) => (
          <div
            key={poll.poll_id}
            className={`p-4 rounded-lg border ${!isOwnProfile && poll.user_matches ? 'border-green-200 bg-green-50' : 'border-gray-200 bg-gray-50'}`}
          >
            <div className="flex items-start justify-between gap-4">
              <div className="flex-grow">
                <p className="font-medium text-gray-900 mb-2">{poll.question}</p>

                {/* Tags */}
                {poll.tags && poll.tags.length > 0 && (
                  <div className="flex flex-wrap gap-1 mb-2">
                    {poll.tags.map((tag) => (
                      <span key={tag} className="text-xs bg-gray-200 text-gray-600 px-2 py-0.5 rounded-full">
                        {tag}
                      </span>
                    ))}
                  </div>
                )}

                {/* Explanation with Agree/Disagree */}
                {poll.explanation && (
                  <div className="mt-3 p-3 bg-white rounded-lg border border-gray-200">
                    <p className="text-sm text-gray-600 italic">&quot;{poll.explanation}&quot;</p>
                    {!isOwnProfile && (
                      <div className="mt-3 pt-2 border-t border-gray-100">
                        {(() => {
                          const hasAgreed = agreedPolls.has(poll.poll_id) || (!disagreedPolls.has(poll.poll_id) && poll.user_matches && poll.user_answered);
                          const hasDisagreed = disagreedPolls.has(poll.poll_id) || (!agreedPolls.has(poll.poll_id) && !poll.user_matches && poll.user_answered);
                          return (
                            <div className="flex items-center gap-2">
                              <button
                                onClick={() => onAgree(poll)}
                                disabled={!!agreeingPoll || hasAgreed}
                                className={`inline-flex items-center gap-1.5 px-4 py-1.5 text-sm font-medium rounded-lg transition-colors disabled:opacity-70 ${
                                  hasAgreed
                                    ? 'bg-green-100 text-green-700 border border-green-300 cursor-default'
                                    : 'text-primary border border-primary/30 hover:bg-primary/10'
                                }`}
                                data-testid={`agree-btn-${poll.poll_id}`}
                              >
                                <MdHandshake className="text-base" />
                                {agreeingPoll === poll.poll_id + '_agree' ? 'Updating...' : hasAgreed ? `Agreed with ${repFirstName}` : 'I agree'}
                              </button>
                              <button
                                onClick={() => onDisagree(poll)}
                                disabled={!!agreeingPoll || hasDisagreed}
                                className={`inline-flex items-center gap-1.5 px-4 py-1.5 text-sm font-medium rounded-lg transition-colors disabled:opacity-70 ${
                                  hasDisagreed
                                    ? 'bg-red-100 text-red-700 border border-red-300 cursor-default'
                                    : 'text-red-500 border border-red-200 hover:bg-red-50'
                                }`}
                                data-testid={`disagree-btn-${poll.poll_id}`}
                              >
                                <MdThumbDown className="text-base" />
                                {agreeingPoll === poll.poll_id + '_disagree' ? 'Updating...' : hasDisagreed ? `Disagreed with ${repFirstName}` : 'I do not agree'}
                              </button>
                            </div>
                          );
                        })()}
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Answer Badge */}
              <div className="flex flex-col items-center gap-1">
                <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                  poll.answer === 'yes' ? 'bg-green-100' : 'bg-red-100'
                }`}>
                  {poll.answer === 'yes' ? (
                    <MdThumbUp className="text-2xl text-green-600" />
                  ) : (
                    <MdThumbDown className="text-2xl text-red-600" />
                  )}
                </div>
                <span className={`text-xs font-medium ${
                  poll.answer === 'yes' ? 'text-green-600' : 'text-red-600'
                }`}>
                  {poll.answer.toUpperCase()}
                </span>
                {!isOwnProfile && poll.user_matches && (
                  <span className="text-xs text-green-600 bg-green-100 px-2 py-0.5 rounded-full">
                    Match!
                  </span>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Load More */}
      {hasMore && (
        <button
          onClick={() => setVisibleCount(prev => prev + PAGE_SIZE)}
          className="mt-4 w-full flex items-center justify-center gap-2 py-3 rounded-lg border border-gray-200 text-sm font-medium text-gray-700 hover:bg-gray-50 hover:border-primary/40 transition-colors"
          data-testid="load-more-polls-btn"
        >
          <MdExpandMore className="text-lg" />
          Load More ({remaining} remaining)
        </button>
      )}
    </div>
  );
}

export default RepPollResponses;
